import { NetworkStatus } from "@apollo/client";
import { useMemo } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { SearchInput } from "types/generated";

import { SEARCH_STACK_TASKS } from "./gql";
import { makeStackTasksPredicates } from "./predicates";

const useSearchStackTasks = (stackId: string, input: Omit<SearchInput, "predicates">) => {
  const predicates = useMemo(
    () => makeStackTasksPredicates(stackId, input.fullTextSearch ?? ""),
    [stackId, input.fullTextSearch]
  );

  const { data, previousData, error, loading, networkStatus } = usePolledQuery(SEARCH_STACK_TASKS, {
    variables: { input: { ...input, fullTextSearch: null, predicates } },
    pollingGroup: PollingIntervalGroups.Lists,
    nextFetchPolicy: "cache-first",
  });

  const searchRuns = (data ?? previousData)?.searchRuns;

  const tasks = useMemo(() => searchRuns?.edges.map((edge) => edge.node.run) ?? [], [searchRuns]);

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  return {
    tasks,
    loadingContent,
    initialLoading: loading && !searchRuns,
    endCursor: searchRuns?.pageInfo.endCursor,
    startCursor: searchRuns?.pageInfo.startCursor,
    error,
    loading,
  };
};

export default useSearchStackTasks;
